import React, { useState, useEffect, useMemo, useRef } from "react";
import { Card, Input, Switch, Select } from "antd";
import { toast } from "react-hot-toast";
import DiffViewer, { DiffMethod } from "react-diff-viewer-continued";
import { useParams, useSearchParams } from "react-router-dom";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useTranslation } from "react-i18next";
import api from "../interceptors/api";
import StandardButton from '../components/StandardButton';
import { localizedPath } from "../utils/paths";
import { splitLines, normalizeText, diffLines, diffStats, unifiedDiff } from "../utils/textDiff";

const FILE_ACCEPT = ".txt,.md,.markdown,.json,.csv,.tsv,.js,.jsx,.ts,.tsx,.html,.css,.xml,.yaml,.yml,.py,.java,.c,.cpp,.h,.go,.rs,.rb,.php,.sh,.sql,.log,.ini,.conf,.diff,.patch,text/*";
const MAX_FILE_BYTES = 5 * 1024 * 1024;

const TextCompare = () => {
  const { t, i18n } = useTranslation();
  const { lang: langParam } = useParams();
  const lang = langParam || i18n.language || "en";
  const [original, setOriginal] = useState(t("original_sample_text"));
  const [modified, setModified] = useState(t("modified_sample_text"));
  const [searchParams] = useSearchParams();
  const [splitView, setSplitView] = useState(true);
  const [disableWordDiff, setDisableWordDiff] = useState(false);
  const [compareMethod, setCompareMethod] = useState(DiffMethod.CHARS);
  const [hideLineNumbers, setHideLineNumbers] = useState(false);
  const [showDiffOnly, setShowDiffOnly] = useState(true);
  const [ignoreCase, setIgnoreCase] = useState(false);
  const [ignoreWhitespace, setIgnoreWhitespace] = useState(false);
  const [ignoreBlankLines, setIgnoreBlankLines] = useState(false);
  const originalFileRef = useRef(null);
  const modifiedFileRef = useRef(null);

  useEffect(() => {
    const shareId = searchParams.get("share");
    if (shareId) {
      api.get(`/text-compare/share/${shareId}`)
        .then((res) => {
          const data = res.data;
          if (data.original !== undefined && data.modified !== undefined) {
            setOriginal(data.original);
            setModified(data.modified);
          }
        })
        .catch(() => {
          toast.error(t('server_error'));
        });
    }
  }, [searchParams, t]);

  // The ignore options are implemented by normalizing both inputs before they
  // reach the diff viewer and the stats, so when any option is on the viewer
  // shows the normalized text (lower-cased / trimmed / blank lines removed),
  // not the raw input.
  const anyIgnore = ignoreCase || ignoreWhitespace || ignoreBlankLines;
  const { viewOld, viewNew, ops } = useMemo(() => {
    const opts = { ignoreCase, ignoreWhitespace, ignoreBlankLines };
    const a = anyIgnore ? normalizeText(original, opts) : original;
    const b = anyIgnore ? normalizeText(modified, opts) : modified;
    return { viewOld: a, viewNew: b, ops: diffLines(splitLines(a), splitLines(b)) };
  }, [original, modified, ignoreCase, ignoreWhitespace, ignoreBlankLines, anyIgnore]);

  const stats = useMemo(() => diffStats(ops), [ops]);
  const identical = stats.added === 0 && stats.removed === 0;

  const countsFor = (text) => t('text_compare_counts', {
    chars: text.length.toLocaleString(lang),
    lines: splitLines(text).length.toLocaleString(lang),
  });

  const handleShare = async () => {
    try {
      const res = await api.post(`/text-compare/share`, { original, modified });
      const data = res.data;
      if (data.id) {
        const sharePath = localizedPath(lang, "/tools/text-compare");
        await navigator.clipboard.writeText(`${window.location.origin}${sharePath}?share=${data.id}`);
        toast.success(t('share_success'));
      } else {
        toast.error(t('share_failed'));
      }
    } catch {
      toast.error(t('share_failed'));
    }
  };

  const swap = () => {
    setOriginal(modified);
    setModified(original);
  };

  const clearBoth = () => {
    setOriginal("");
    setModified("");
  };

  const loadFile = (setter) => (e) => {
    const input = e.target;
    const file = input.files && input.files[0];
    if (!file) return;
    if (file.size > MAX_FILE_BYTES) {
      toast.error(t('text_compare_file_too_large'));
      input.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setter(String(reader.result || ""));
    reader.onerror = () => toast.error(t('text_compare_file_error'));
    reader.readAsText(file);
    // Reset so selecting the same file again still fires onChange.
    input.value = "";
  };

  const getPatch = () => unifiedDiff(ops, { oldName: "original.txt", newName: "modified.txt" });

  const copyDiff = async () => {
    const patch = getPatch();
    if (!patch) {
      toast.error(t('text_compare_no_diff'));
      return;
    }
    try {
      await navigator.clipboard.writeText(patch);
      toast.success(t('copy_success'));
    } catch {
      toast.error(t('failed_to_copy'));
    }
  };

  const downloadDiff = () => {
    const patch = getPatch();
    if (!patch) {
      toast.error(t('text_compare_no_diff'));
      return;
    }
    const blob = new Blob([patch], { type: "text/x-diff;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "text-compare.diff";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const renderInput = ({ id, label, placeholder, value, setValue, fileRef }) => (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <label htmlFor={id} className="block font-semibold text-gray-700">{label}</label>
        <StandardButton
          onClick={() => fileRef.current && fileRef.current.click()}
          type="secondary"
          variant="outlined"
          size="small"
          aria-label={t('text_compare_load_file_into', { target: label })}
        >
          <span>{t('text_compare_load_file')}</span>
        </StandardButton>
        <input
          ref={fileRef}
          type="file"
          accept={FILE_ACCEPT}
          className="hidden"
          tabIndex={-1}
          aria-hidden="true"
          onChange={loadFile(setValue)}
        />
      </div>
      <Input.TextArea
        id={id}
        rows={6}
        placeholder={placeholder}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="font-mono"
      />
      <div className="text-xs text-gray-500 mt-1" aria-live="polite">{countsFor(value)}</div>
    </div>
  );

  const ignoreOptions = [
    { id: "text-compare-ignore-case", label: t('text_compare_ignore_case'), checked: ignoreCase, onChange: setIgnoreCase },
    { id: "text-compare-ignore-whitespace", label: t('text_compare_ignore_whitespace'), checked: ignoreWhitespace, onChange: setIgnoreWhitespace },
    { id: "text-compare-ignore-blank-lines", label: t('text_compare_ignore_blank_lines'), checked: ignoreBlankLines, onChange: setIgnoreBlankLines },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-0">
      <Header />
      <div className="max-w-5xl mx-auto">
        <Card className="p-6 rounded-xl shadow-lg space-y-6">
          <div>
            <h1 className="text-2xl font-bold text-indigo-700">{t('text_compare_name')}</h1>
            <p className="text-gray-600 mt-1">{t('text_compare_intro')}</p>
          </div>

          {renderInput({
            id: "text-compare-original",
            label: t('original_text'),
            placeholder: t('original_text_placeholder'),
            value: original,
            setValue: setOriginal,
            fileRef: originalFileRef,
          })}
          {renderInput({
            id: "text-compare-modified",
            label: t('modified_text'),
            placeholder: t('modified_text_placeholder'),
            value: modified,
            setValue: setModified,
            fileRef: modifiedFileRef,
          })}

          <div className="flex flex-wrap gap-3">
            <StandardButton onClick={swap} type="secondary" variant="outlined" size="small">
              <span>{t('text_compare_swap')}</span>
            </StandardButton>
            <StandardButton onClick={clearBoth} type="secondary" variant="outlined" size="small" danger>
              <span>{t('text_compare_clear_both')}</span>
            </StandardButton>
          </div>

          <Card className="p-4 mb-6" size="small" title={t('settings')}>
            <fieldset className="mb-4">
              <legend className="text-sm font-medium text-gray-700 mb-2">{t('text_compare_options_label')}</legend>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {ignoreOptions.map((o) => (
                  <div key={o.id} className="flex items-center gap-2">
                    <Switch id={o.id} checked={o.checked} onChange={o.onChange} aria-label={o.label} size="small" />
                    <label htmlFor={o.id} className="text-sm text-gray-700 cursor-pointer">{o.label}</label>
                  </div>
                ))}
              </div>
            </fieldset>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Switch
                checked={splitView}
                onChange={setSplitView}
                checkedChildren={t('split_view')}
                unCheckedChildren={t('inline_view')}
                aria-label={splitView ? t('split_view') : t('inline_view')}
              />
              <Switch
                checked={disableWordDiff}
                onChange={setDisableWordDiff}
                checkedChildren={t('word_diff_off')}
                unCheckedChildren={t('word_diff_on')}
                aria-label={disableWordDiff ? t('word_diff_off') : t('word_diff_on')}
              />
              <Switch
                checked={hideLineNumbers}
                onChange={setHideLineNumbers}
                checkedChildren={t('hide_line_numbers')}
                unCheckedChildren={t('show_line_numbers')}
                aria-label={hideLineNumbers ? t('hide_line_numbers') : t('show_line_numbers')}
              />
              <Switch
                checked={showDiffOnly}
                onChange={setShowDiffOnly}
                checkedChildren={t('show_diff_only')}
                unCheckedChildren={t('show_full_text')}
                aria-label={showDiffOnly ? t('show_diff_only') : t('show_full_text')}
              />

              <div>
                <label htmlFor="text-compare-method" className="text-sm">{t('compare_method')}</label>
                <Select id="text-compare-method" value={compareMethod} onChange={setCompareMethod} className="w-full">
                  <Select.Option value={DiffMethod.CHARS}>{t('chars')}</Select.Option>
                  <Select.Option value={DiffMethod.WORDS}>{t('words')}</Select.Option>
                  <Select.Option value={DiffMethod.LINES}>{t('lines')}</Select.Option>
                </Select>
              </div>
            </div>
          </Card>

          <h2 className="text-lg font-medium mt-4">{t('differences')}</h2>

          <div
            className="flex flex-wrap items-center gap-3 p-3 rounded-md border bg-white text-sm"
            role="status"
            aria-label={t('text_compare_summary_label')}
          >
            {identical ? (
              <span className="font-semibold text-green-700">{t('text_compare_identical')}</span>
            ) : (
              <>
                <span className="px-2 py-1 rounded bg-green-50 text-green-800 border border-green-200">
                  +{stats.added.toLocaleString(lang)} {t('text_compare_stat_added')}
                </span>
                <span className="px-2 py-1 rounded bg-red-50 text-red-800 border border-red-200">
                  −{stats.removed.toLocaleString(lang)} {t('text_compare_stat_removed')}
                </span>
                <span className="px-2 py-1 rounded bg-gray-50 text-gray-700 border border-gray-200">
                  {stats.unchanged.toLocaleString(lang)} {t('text_compare_stat_unchanged')}
                </span>
              </>
            )}
          </div>
          {anyIgnore && (
            <p className="text-xs text-gray-500">{t('text_compare_normalized_note')}</p>
          )}

          {!identical && (
            <DiffViewer
              oldValue={viewOld}
              newValue={viewNew}
              splitView={splitView}
              disableWordDiff={disableWordDiff}
              compareMethod={compareMethod}
              hideLineNumbers={hideLineNumbers}
              showDiffOnly={showDiffOnly}
            />
          )}

          {(original || modified) && (
            <div className="mt-4 flex flex-wrap gap-3">
              <StandardButton
                onClick={copyDiff}
                type="secondary"
                variant="outlined"
                size="small"
                disabled={identical}
              >
                <span>{t('text_compare_copy_diff')}</span>
              </StandardButton>
              <StandardButton
                onClick={downloadDiff}
                type="secondary"
                variant="outlined"
                size="small"
                disabled={identical}
              >
                <span>{t('text_compare_download_diff')}</span>
              </StandardButton>
              <StandardButton
                onClick={handleShare}
                type="secondary"
                variant="outlined"
                size="small"
              >
                {t('share_button')}
              </StandardButton>
            </div>
          )}
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default TextCompare;
